/**
 * usePermissions Hook
 * 
 * Simplified permission checking hooks for common use cases
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectPermissions, selectIsAuthenticated } from '../store/authSelectors';
import type { RootState } from '@core/store/store';

/**
 * Check if user has a single permission
 *
 * @param permission - Permission string (e.g., "Fleet.Vehicles.Read")
 * @returns true if user has the permission
 *
 * @example
 * const canRead = usePermission(Permissions.Fleet.Vehicles.Read);
 * if (canRead) {
 *   // Show read UI
 * }
 */
export const usePermission = (permission: string): boolean => {
  const permissions = useSelector(selectPermissions);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return useMemo(() => {
    if (!isAuthenticated) return false;
    return permissions.includes(permission);
  }, [permissions, permission, isAuthenticated]);
};

/**
 * Check multiple permissions at once
 *
 * @param permissionsToCheck - Array of permission strings
 * @returns Object with hasAny and hasAll boolean properties
 *
 * @example
 * const { hasAny, hasAll } = usePermissions([
 *   Permissions.Fleet.Vehicles.Read,
 *   Permissions.Fleet.Vehicles.Create,
 * ]);
 * 
 * if (hasAny) {
 *   // User has at least one permission
 * }
 * 
 * if (hasAll) {
 *   // User has all permissions
 * }
 */
export const usePermissions = (permissionsToCheck: string[]) => {
  const permissions = useSelector(selectPermissions);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return useMemo(() => {
    if (!isAuthenticated) {
      return { hasAny: false, hasAll: false };
    }

    const results = permissionsToCheck.map((p) => permissions.includes(p));

    return {
      hasAny: results.some(Boolean),
      hasAll: results.every(Boolean),
    };
  }, [permissions, permissionsToCheck, isAuthenticated]);
};

/**
 * Check if user has any role
 * 
 * @param roles - Array of role names
 * @returns true if user has at least one role
 * 
 * @example
 * const isAdmin = useHasRole(['Admin', 'SuperAdmin']);
 */
export const useHasRole = (roles: string[]): boolean => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);

  return useMemo(() => {
    if (!isAuthenticated || !user) return false;
    return roles.some((role) => user.roles?.includes(role));
  }, [isAuthenticated, user, roles]);
};
